import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { CanvasService } from './canvas.service';
import { appendIconChildren } from './connector/connector-utils';
import { MatIconRegistry } from '@angular/material/icon';
import { ElementAttribute } from '@models/editor';
import { ZoomService } from './zoom.service';
import { ConnectorPlacementService, ConnectorType } from './connector-placement.service';

@Injectable({
    providedIn: 'root',
})
export class ElementOrientationService {
    private get canvas() {
        return this.canvasService.getCanvas();
    }

    constructor(
        private canvasService: CanvasService,
        private matIconRegistry: MatIconRegistry,
        private zoomService: ZoomService,
        private connectorPlacementService: ConnectorPlacementService
    ) {}

    applyPumpOrientation(group: SVGGElement, iconName: string): Observable<void> {
        return new Observable(observer => {
            this.matIconRegistry
                .getNamedSvgIcon(iconName)
                .pipe(take(1))
                .subscribe((svgEl: SVGElement) => {
                    const iconRadius = (svgEl as SVGSVGElement).viewBox.baseVal.width / 2;
                    const circle = group.querySelector('circle');
                    const r = circle ? Number(circle.getAttribute('r')) : iconRadius;
                    const scale = r / iconRadius;
                    const cx = circle ? Number(circle.getAttribute('cx')) : iconRadius * scale;
                    const cy = circle ? Number(circle.getAttribute('cy')) : iconRadius * scale;
                    const tx = cx - iconRadius * scale;
                    const ty = cy - iconRadius * scale;
                    const offsetTransform = `matrix(${scale}, 0, 0, ${scale}, ${tx}, ${ty})`;

                    const colorByClass: Record<string, { fill: string; stroke: string }> = {};

                    Array.from(group.querySelectorAll('path, circle.pump-center-circle')).forEach(c => {
                        const fill = c.getAttribute('fill') ?? 'none';
                        const stroke = c.getAttribute('stroke') ?? 'none';
                        c.classList.forEach(cls => {
                            colorByClass[cls] = { fill, stroke };
                        });
                    });

                    const srcList = Array.from(svgEl.querySelectorAll('path, circle.pump-center-circle'));
                    const dstList = Array.from(group.querySelectorAll('path, circle.pump-center-circle'));

                    srcList.forEach((src, i) => {
                        const dst = dstList[i];
                        if (!dst) return;

                        if (src.tagName === 'path') {
                            dst.setAttribute('d', src.getAttribute('d') || '');
                        } else if (src.tagName === 'circle') {
                            dst.setAttribute('cx', src.getAttribute('cx') || '');
                            dst.setAttribute('cy', src.getAttribute('cy') || '');
                            dst.setAttribute('r', src.getAttribute('r') || '');
                        }

                        dst.setAttribute('transform', offsetTransform);

                        let colorApplied = false;
                        dst.classList.forEach(cls => {
                            if (colorByClass[cls] && !colorApplied) {
                                dst.setAttribute('fill', colorByClass[cls].fill);
                                dst.setAttribute('stroke', colorByClass[cls].stroke);
                                colorApplied = true;
                            }
                        });

                        if (!colorApplied) {
                            dst.setAttribute('fill', 'none');
                            dst.setAttribute('stroke', 'none');
                        }
                    });

                    group.setAttribute(ElementAttribute.pumpIcon, iconName);
                    this.canvas.call('changed', [group]);
                    this.canvas.selectOnly([group], true);
                    observer.next();
                    observer.complete();
                });
        });
    }

    applyConnectorOrientation(group: SVGGElement, iconName: string): Observable<void> {
        return new Observable(observer => {
            const svgRoot = this.canvasService.getSvgRoot() as unknown as SVGSVGElement;
            const layer = svgRoot.querySelector('.layer') as unknown as SVGGraphicsElement;
            const currentIconName = group.getAttribute(ElementAttribute.connectorIcon) ?? '';
            const isSameIcon = currentIconName === iconName;
            const screenRect = (group as unknown as Element).getBoundingClientRect();
            const layerInv = layer.getScreenCTM()!.inverse();
            const tl = new DOMPoint(screenRect.left, screenRect.top).matrixTransform(layerInv);
            const br = new DOMPoint(screenRect.right, screenRect.bottom).matrixTransform(layerInv);
            const renderedCx = (tl.x + br.x) / 2;
            const renderedCy = (tl.y + br.y) / 2;

            const oldMatrix = group.transform.baseVal.consolidate()?.matrix;
            const oldSx = oldMatrix?.a ?? 1;
            const oldSy = oldMatrix?.d ?? 1;

            const CONNECTOR_ICON_ORIENTATION: Record<string, 'horizontal' | 'vertical' | 'square'> = {
                'lines-horizontal': 'horizontal',
                'lines-vertical': 'vertical',
                'direction-right': 'horizontal',
                'direction-left': 'horizontal',
                'direction-down': 'vertical',
                'direction-direction-3': 'vertical',
                'end-point-right': 'horizontal',
                'end-point-left': 'horizontal',
                'end-point-up': 'vertical',
                'end-point-down': 'vertical',
            };
            const oldOri = CONNECTOR_ICON_ORIENTATION[currentIconName] ?? 'square';
            const newOri = CONNECTOR_ICON_ORIENTATION[iconName] ?? 'square';
            const shouldSwap =
                !isSameIcon &&
                ((oldOri === 'horizontal' && newOri === 'vertical') ||
                    (oldOri === 'vertical' && newOri === 'horizontal'));

            const newSx = shouldSwap ? oldSy : oldSx;
            const newSy = shouldSwap ? oldSx : oldSy;

            const connectorType = group.getAttribute(ElementAttribute.connectorType);
            const keepSize = connectorType === ConnectorType.STRAIGHT || connectorType === ConnectorType.DIRECTION;
            const oldSize = keepSize ? this.connectorPlacementService.getGroupBBoxExcludingIgnored(group) : null;
            const oldCornerThickness = this.connectorPlacementService.getConnectorThickness(group);

            this.matIconRegistry
                .getNamedSvgIcon(iconName)
                .pipe(take(1))
                .subscribe((svgEl: SVGElement) => {
                    while (group.firstChild) {
                        group.removeChild(group.firstChild);
                    }
                    const svgRootDefs =
                        svgRoot.querySelector('defs') ??
                        svgRoot.insertBefore(
                            document.createElementNS('http://www.w3.org/2000/svg', 'defs'),
                            svgRoot.firstChild
                        );
                    appendIconChildren(svgEl, group, () => this.canvas.getNextId(), svgRootDefs);

                    group.setAttribute(ElementAttribute.connectorIcon, iconName);
                    const newScale = { sx: Math.abs(newSx), sy: Math.abs(newSy) };
                    if (oldSize) {
                        this.connectorPlacementService.setConnectorSize(
                            group,
                            shouldSwap
                                ? { width: oldSize.height, height: oldSize.width }
                                : { width: oldSize.width, height: oldSize.height },
                            newScale
                        );
                    }
                    if (oldCornerThickness) {
                        this.connectorPlacementService.setConnectorThickness(group, oldCornerThickness, newScale);
                    }
                    const nb = (group as any).getBBox();

                    const newTx = renderedCx - newSx * (nb.x + nb.width / 2);
                    const newTy = renderedCy - newSy * (nb.y + nb.height / 2);
                    group.setAttribute('transform', `matrix(${newSx}, 0, 0, ${newSy}, ${newTx}, ${newTy})`);
                    this.canvas.call('changed', [group]);
                    this.canvas.selectOnly([group], true);
                    observer.next();
                    observer.complete();
                });
        });
    }

    private static readonly VALVE_BASE_RADIUS = 10;

    private readonly VALVE_PATHS = {
        static: {
            horizontal:
                'M10.333 13.75a1 1 0 0 0-.6.2L6.6 16.3a1 1 0 0 1-1.6-.8V9a1 1 0 0 1 1.6-.8l3.133 2.35a1 1 0 0 0 .6.2h3.334a1 1 0 0 0 .6-.2L17.4 8.2A1 1 0 0 1 19 9v6.5a1 1 0 0 1-1.6.8l-3.133-2.35a1 1 0 0 0-.6-.2h-3.334z',
            vertical:
                'M10.75 10.333a1 1 0 0 0-.2-.6L8.2 6.6A1 1 0 0 1 9 5h6.5a1 1 0 0 1 .8 1.6l-2.35 3.133a1 1 0 0 0-.2.6v3.334a1 1 0 0 0 .2.6L16.3 17.4a1 1 0 0 1-.8 1.6H9a1 1 0 0 1-.8-1.6l2.35-3.133a1 1 0 0 0 .2-.6v-3.334z',
        },
        closed: {
            horizontal:
                'M14 11.25a1 1 0 0 1 .4-.8l3-2.25A1 1 0 0 1 19 9v6.5a1 1 0 0 1-1.6.8l-3-2.25a1 1 0 0 1-.4-.8v-2zM10 11.25a1 1 0 0 0-.4-.8l-3-2.25A1 1 0 0 0 5 9v6.5a1 1 0 0 0 1.6.8l3-2.25a1 1 0 0 0 .4-.8v-2z',
            vertical:
                'M11.25 10a1 1 0 0 1-.8-.4l-2.25-3A1 1 0 0 1 9 5h6.5a1 1 0 0 1 .8 1.6l-2.25 3a1 1 0 0 1-.8.4h-2zM11.25 14a1 1 0 0 0-.8.4l-2.25 3A1 1 0 0 0 9 19h6.5a1 1 0 0 0 .8-1.6l-2.25-3a1 1 0 0 0-.8-.4h-2z',
        },
    };

    private getCircleGeometry(group: SVGGElement): { r: number; cx: number; cy: number } {
        const circle = group.querySelector('circle');
        return {
            r: circle ? Number(circle.getAttribute('r')) : ElementOrientationService.VALVE_BASE_RADIUS,
            cx: circle ? Number(circle.getAttribute('cx')) : 0,
            cy: circle ? Number(circle.getAttribute('cy')) : 0,
        };
    }

    private centerPathsOnCircle(paths: SVGPathElement[], geometry: { r: number; cx: number; cy: number }): void {
        if (!paths.length) return;

        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;
        for (const path of paths) {
            const bbox = path.getBBox();
            minX = Math.min(minX, bbox.x);
            minY = Math.min(minY, bbox.y);
            maxX = Math.max(maxX, bbox.x + bbox.width);
            maxY = Math.max(maxY, bbox.y + bbox.height);
        }

        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;
        const scale = geometry.r / ElementOrientationService.VALVE_BASE_RADIUS;
        const dx = geometry.cx - scale * centerX;
        const dy = geometry.cy - scale * centerY;
        const transform = `matrix(${scale}, 0, 0, ${scale}, ${dx}, ${dy})`;
        for (const path of paths) {
            path.setAttribute('transform', transform);
        }
    }

    applyValveOrientation(
        group: SVGGElement,
        orientation: 'horizontal' | 'vertical',
        state: 'static' | 'closed' = 'static'
    ): void {
        const path = group.querySelector('path') as SVGPathElement;
        if (!path) return;

        path.setAttribute('d', this.VALVE_PATHS[state][orientation]);
        this.centerPathsOnCircle([path], this.getCircleGeometry(group));

        group.setAttribute('data-icreate-valve-way', '2way');
        group.setAttribute('data-icreate-valve-orientation', orientation);
        group.setAttribute('data-icreate-valve-state', state);
        this.canvas.call('changed', [group]);
    }

    applyValveIcon3Way(
        group: SVGGElement,
        iconName: string,
        orientation: 'horizontal' | 'vertical',
        state: 'static' | 'closed' = 'static'
    ): Observable<void> {
        return new Observable(observer => {
            this.matIconRegistry
                .getNamedSvgIcon(iconName)
                .pipe(take(1))
                .subscribe((svgEl: SVGElement) => {
                    const geometry = this.getCircleGeometry(group);

                    const existingPath = group.querySelector('path');
                    const bodyFill = existingPath?.getAttribute('fill') || '#607d8b';

                    const circleClone = group.querySelector('circle')?.cloneNode(true) as SVGCircleElement;

                    while (group.firstChild) {
                        group.removeChild(group.firstChild);
                    }

                    if (circleClone) {
                        group.appendChild(circleClone);
                    }

                    const svgRoot = this.canvasService.getSvgRoot() as unknown as SVGSVGElement;
                    const svgRootDefs =
                        svgRoot.querySelector('defs') ??
                        svgRoot.insertBefore(
                            document.createElementNS('http://www.w3.org/2000/svg', 'defs'),
                            svgRoot.firstChild
                        );

                    appendIconChildren(svgEl, group, () => this.canvas.getNextId(), svgRootDefs);

                    const iconPaths = Array.from(group.querySelectorAll('path')) as SVGPathElement[];
                    this.centerPathsOnCircle(iconPaths, geometry);
                    for (const path of iconPaths) {
                        path.setAttribute('fill', bodyFill);
                        path.setAttribute('stroke', 'none');
                    }

                    group.setAttribute('data-icreate-valve-way', '3way');
                    group.setAttribute('data-icreate-valve-orientation', orientation);
                    group.setAttribute('data-icreate-valve-state', state);
                    this.canvas.call('changed', [group]);
                    this.canvas.selectOnly([group], true);
                    observer.next();
                    observer.complete();
                });
        });
    }

    getPopupPosition(element: SVGElement | null): { x: number; y: number } | null {
        if (!element) return null;
        const elemRect = element.getBoundingClientRect();
        const containerRect = document.getElementById('editorContainer')?.getBoundingClientRect();
        if (!containerRect) return null;
        return {
            x: elemRect.left - containerRect.left + elemRect.width / 2,
            y: elemRect.top - containerRect.top,
        };
    }

    watchElementPosition(element: SVGGElement): Observable<{ x: number; y: number }> {
        return new Observable(observer => {
            let lastX = 0;
            let lastY = 0;

            const updatePosition = () => {
                const pos = this.getPopupPosition(element);
                if (pos) {
                    // Only emit if position changed to avoid excessive updates
                    if (pos.x !== lastX || pos.y !== lastY) {
                        lastX = pos.x;
                        lastY = pos.y;
                        observer.next(pos);
                    }
                }
            };

            // Emit initial position immediately
            updatePosition();

            // Use MutationObserver for attribute changes
            const mo = new MutationObserver(() => updatePosition());
            mo.observe(element, { attributes: true });
            let parent = element.parentElement;
            while (parent) {
                mo.observe(parent, { attributes: true });
                parent = parent.parentElement;
            }

            // Use polling for drag operations (50ms interval)
            const pollInterval = setInterval(() => updatePosition(), 50);

            // Also subscribe to zoom changes
            const zoomSub = this.zoomService.onZoom().subscribe(() => updatePosition());

            return () => {
                mo.disconnect();
                clearInterval(pollInterval);
                zoomSub.unsubscribe();
            };
        });
    }
}
